import { useAccount } from "wagmi"
import useSwapParams from "./useSwapParams"
import { useDebounce } from "./useDebounce"
import { tryParseAmount, USDC, USDT, WETH9 } from "../packages/currency"
import { ZERO } from "../packages/math"
import useSettings from "./useSettings"
import { useQuery } from "@tanstack/react-query"
import { getXFusionTrade } from "../utils/trade"
import { usePoolsCodeMap } from "../packages/pools"
import { ChainId } from "../packages/chain"

const useSwapTrade = () => {
  const { amountIn, tokenIn, tokenOut } = useSwapParams()
  const { address } = useAccount()
  const { slippage } = useSettings()

  const isFeeExcluded =
    tokenIn &&
    tokenOut &&
    ((tokenIn.isNative &&
      !tokenOut.isNative &&
      tokenOut.address.toLowerCase() ===
        WETH9[ChainId.INK].address.toLowerCase()) ||
      (tokenOut.isNative &&
        !tokenIn.isNative &&
        tokenIn.address.toLowerCase() ===
          WETH9[ChainId.INK].address.toLowerCase()) ||
      ([USDC[ChainId.INK], USDT[ChainId.INK]].findIndex(
        (item) =>
          item.address.toLowerCase() === tokenIn.wrapped.address.toLowerCase()
      ) > -1 &&
        [USDC[ChainId.INK], USDT[ChainId.INK]].findIndex(
          (item) =>
            item.address.toLowerCase() ===
            tokenOut.wrapped.address.toLowerCase()
        ) > -1))

  const parsedAmount = useDebounce(tryParseAmount(amountIn, tokenIn), 200)
  const feeAmount = parsedAmount
    ?.multiply(BigInt(isFeeExcluded ? 0 : 0.25 * 10000))
    ?.divide(1000000n)

  const { data: poolsCodeMap } = usePoolsCodeMap({
    chainId: ChainId.INK,
    currencyA: tokenIn,
    currencyB: tokenOut,
    enabled: Boolean(parsedAmount?.greaterThan(0)),
  })

  const trade = useQuery({
    queryKey: [
      "smart-router",
      tokenIn,
      tokenOut,
      parsedAmount,
      slippage,
      poolsCodeMap,
      address,
    ],
    queryFn: async () => {
      try {
        if (
          !tokenIn ||
          !tokenOut ||
          !parsedAmount ||
          !parsedAmount.greaterThan(ZERO) ||
          !feeAmount
        ) {
          return undefined
        }

        const trades = await getXFusionTrade(
          tokenIn,
          tokenOut,
          address ?? "0xec288809063df839a62a3a61dd28f2142592b170",
          slippage,
          parsedAmount.subtract(feeAmount).quotient.toString(),
          poolsCodeMap
        )

        return {
          ...trades,
          isFeeExcluded,
          feeAmount: feeAmount?.quotient ?? 0n,
          value: tokenIn.isNative ? parsedAmount.quotient : undefined,
        }
      } catch (err) {
        console.log(err)
      }
    },
    refetchInterval: 15000,
    enabled:
      Boolean(tokenIn) &&
      Boolean(tokenOut) &&
      Boolean(parsedAmount?.greaterThan(ZERO)),
    refetchOnWindowFocus: false,
  })
  return trade
}

export default useSwapTrade
